/**
 * AdminListScaffold — the whole list-page shape for a telephony admin resource.
 *
 * Every one of these pages (Blacklist Callers, Speed Dial, VIP Callers, Special
 * Inbound Routes, Play N Go, External Call Forwarding, Advance Call Flows,
 * Conferences, Time Conditions, Transfer, IVR, DIDs, SIP Trunks, Queues,
 * Outbound Caller ID) hits a common endpoint contract:
 *
 *   GET <endpoint>?search&page&limit[&includeDeleted] -> { <key>, summary, pagination }
 *   DELETE <endpoint>/:id            -> 204
 *   POST   <endpoint>/:id/restore    -> 200  (when canRestore is set)
 *
 * …and renders the same chrome as the users-tab list screen: `AdminListScreenLayout`,
 * a header with a primary action, `SummaryCards`, `ListToolbar`, `DataTable` with
 * a shared actions column, a delete confirm, and a create/edit/view drawer.
 *
 * Search and pagination are SERVER-side (the endpoint slices), so the search box
 * is debounced rather than filtering in memory.
 */
import { useState, useEffect, useCallback, useMemo, useRef, type JSX, type ReactNode } from 'react'
import { Column } from 'primereact/column'

import AdminListScreenLayout from '../layouts/AdminListScreenLayout.js'
import DataTable from '../ui/DataTable.js'
import SummaryCards from './SummaryCards.js'
import ListToolbar from './ListToolbar.js'
import ActionButtons from './ActionButtons.js'
import { useConfirmDialog } from '../ui/ConfirmDialog.js'
import { useToast } from '../system/ToastProvider.js'
import { api } from '../../api/client.js'
import type { ColumnDef } from './ResourceTable.js'

/** Rows per page. Server-side — the endpoint slices, not the client. */
const PAGE_SIZE = 10
/** Debounce on the search box so typing doesn't fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 300
/** How long the reload-required notice stays visible after a save/delete. */
const RELOAD_NOTICE_MS = 8000

/**
 * `totalActive` / `totalInactive` are consistent across most of these
 * endpoints, but not all (audio-files reports MOH/voice-file splits instead of
 * an inactive count), so they are optional and a caller with a different shape
 * passes `tiles` to render its own row.
 */
interface ListSummary {
  totalActive?: number
  totalInactive?: number
  [key: string]: number | undefined
}

interface ListPagination {
  total: number
  page: number
  limit: number
  totalPages: number
}

interface SummaryCard {
  label: string
  value: number
  accentColor: string
}

/** What `renderDrawer` receives — wire these straight onto the drawer's props. */
export interface DrawerRenderProps<T> {
  open: boolean
  /** The row being edited/viewed; `null` = create mode. */
  item: T | null
  readOnly: boolean
  onClose: () => void
  onSaved: () => void
  /**
   * Refresh the list without closing the drawer. Use when a sub-resource
   * changes (e.g. paging group members) and the list's counts need to
   * update, but the drawer should stay open.
   */
  onRefresh?: () => void
  /**
   * Update the editing item without closing the drawer. Use after a create
   * that should transition into edit mode (e.g. Queues: create the queue,
   * then switch to the Members tab without closing).
   */
  onItemChange?: (item: T) => void
}

export interface AdminListScaffoldProps<T extends { id: string }> {
  title: string
  description: string
  /** Base API path, e.g. `/admin/telephone-management/blacklist-callers`. */
  endpoint: string
  /**
   * Key in the list response that holds the items array. Most endpoints use
   * `items`; some older ones return `dids`, `trunks`, `numbers`, `queues`, etc.
   * Default: `'items'`.
   */
  responseKey?: string
  /** Primary button label, e.g. "Add Blacklist Caller". */
  addLabel: string
  /** Table card heading, e.g. "All Blacklist Callers". */
  tableTitle: string
  searchPlaceholder: string
  /** Accessible name for the search input, e.g. "Search blacklist callers". */
  searchAriaLabel?: string
  /** PrimeIcons class for the empty state, e.g. `pi pi-ban`. */
  emptyIcon?: string
  /** Shown in the empty state when nothing exists yet. */
  emptyDescription: string
  /** Noun used in the empty state + toasts, e.g. "blacklist caller". */
  entityLabel: string
  columns: ColumnDef<T>[]
  /** Reads this resource's own total out of the summary payload. */
  totalFrom: (summary: ListSummary) => number
  /** Label for the first summary card, e.g. "Total blacklisted". */
  totalLabel: string
  /** PrimeIcons class for the total tile (kept for backward compatibility). */
  totalIcon?: string
  /**
   * Replaces the default summary-cards row. Use when this resource's `summary`
   * doesn't carry `totalActive`/`totalInactive` — rendering those as 0 would
   * read as real counts. `totalFrom`/`totalLabel`/`totalIcon` are then unused.
   */
  tiles?: (summary: ListSummary | null) => ReactNode
  /** Confirm-dialog body for a delete. */
  deleteMessage: (item: T) => string
  /** The create/edit/view drawer for this resource. */
  renderDrawer: (props: DrawerRenderProps<T>) => ReactNode
  /** Extra controls rendered in the table toolbar, right of the refresh button. */
  toolbarActions?: ReactNode
  /**
   * When true, soft-deleted rows show a Restore button instead of Delete.
   * Requires `isDeleted` to identify deleted rows.
   */
  canRestore?: boolean
  /** Predicate: is this row soft-deleted? Used with `canRestore`. */
  isDeleted?: (item: T) => boolean
  /**
   * When true, renders a "Show deleted" checkbox in the toolbar. The scaffold
   * manages the state and appends `includeDeleted=true` to the fetch.
   */
  includeDeletedToggle?: boolean
  /**
   * When provided, a notice bar with this text is shown for ~8s after every
   * successful save or delete (used by SIP Trunks for the telephony-reload
   * notice).
   */
  reloadNoticeText?: string
  /**
   * Extra query params (pre-built, URL-encoded string) appended to every fetch.
   * Use for filters like `routingStrategy=RING_ALL` that live in the parent
   * page's state. Changes to this string trigger a refetch.
   */
  extraSearchParams?: string
  /**
   * When true, the "Add" button and row actions are hidden — the page is
   * read-only for this user. The drawer can still be opened in view mode.
   */
  canManage?: boolean
  /**
   * Override the delete URL. By default the scaffold deletes via
   * `${endpoint}/${item.id}`, but resources whose list endpoint is
   * scoped (e.g. Follow-Me's `/follow-me/:agentId`) need a different
   * path for deletes (e.g. `/follow-me/:id`).
   */
  deleteEndpoint?: (item: T) => string
  /**
   * When true, the fetch sends `offset` instead of `page` (some older
   * endpoints like `/queues` use offset-based pagination). The response
   * is expected to carry `total` at the root level rather than inside a
   * `pagination` object.
   */
  useOffsetPagination?: boolean
  /**
   * When true, the endpoint is fetched once without search/page/limit
   * params. Search filtering and pagination are done client-side from the
   * full list. Use for endpoints that return all items at once (e.g.
   * `/paging/groups`, `/agents/hot-desk/status`).
   */
  clientSide?: boolean
  /**
   * Client-side search predicate. When `clientSide` is true, the scaffold
   * filters the full item list through this function on every keystroke.
   * If omitted, no filtering is applied (the search box is hidden).
   */
  searchFilter?: (item: T, query: string) => boolean
  /**
   * Replaces the default summary-cards row in `clientSide` mode. Receives
   * the full unfiltered item list so counts can be derived locally.
   */
  clientSideTiles?: (items: T[]) => ReactNode
  /**
   * Custom action buttons rendered in the Actions column alongside or
   * instead of the default View/Edit/Delete/Restore buttons (controlled by
   * `hideRowActions`). Use for resource-specific actions like "Log Out"
   * (hot desking) or "Page" (paging broadcast).
   */
  customActions?: (row: T) => ReactNode
  /**
   * When true, the default View/Edit/Delete/Restore buttons are hidden
   * and only `customActions` (if provided) are rendered.
   */
  hideRowActions?: boolean
  /**
   * Called whenever the fetched item list changes (after every refetch).
   * In `clientSide` mode, this receives the full unfiltered list. Use to
   * derive values the drawer needs (e.g. hot-desking's active agent IDs).
   */
  onDataChange?: (items: T[]) => void
}

export function AdminListScaffold<T extends { id: string }>({
  title,
  description,
  endpoint,
  responseKey = 'items',
  addLabel,
  tableTitle,
  searchPlaceholder,
  searchAriaLabel,
  emptyDescription,
  entityLabel,
  columns,
  totalFrom,
  totalLabel,
  tiles,
  deleteMessage,
  renderDrawer,
  toolbarActions,
  canRestore = false,
  isDeleted,
  includeDeletedToggle = false,
  reloadNoticeText,
  extraSearchParams,
  canManage = true,
  deleteEndpoint,
  useOffsetPagination = false,
  clientSide = false,
  searchFilter,
  clientSideTiles,
  customActions,
  hideRowActions = false,
  onDataChange,
}: AdminListScaffoldProps<T>): JSX.Element {
  const { confirm, Dialog } = useConfirmDialog()
  const toast = useToast()

  const [items, setItems] = useState<T[]>([])
  /** In clientSide mode, the full unfiltered list is kept separately. */
  const [allItems, setAllItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [summary, setSummary] = useState<ListSummary | null>(null)
  const [pagination, setPagination] = useState<ListPagination | null>(null)
  const [offset, setOffset] = useState(0)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  /** Root-level `total` for offset-based endpoints (e.g. /queues). */
  const [rootTotal, setRootTotal] = useState(0)

  // --- Drawer state --------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<T | null>(null)
  const [viewOnly, setViewOnly] = useState(false)

  // --- Reload notice state -------------------------------------------------
  const [showReloadNotice, setShowReloadNotice] = useState(false)
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    }
  }, [])

  function flashReloadNotice(): void {
    if (!reloadNoticeText) return
    setShowReloadNotice(true)
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    reloadTimerRef.current = setTimeout(() => {
      setShowReloadNotice(false)
      reloadTimerRef.current = null
    }, RELOAD_NOTICE_MS)
  }

  const currentPage = useMemo(
    () => (pagination ? pagination.page : Math.floor(offset / PAGE_SIZE) + 1),
    [pagination, offset],
  )

  const total = useMemo(() => {
    if (pagination) return pagination.total
    // Offset-based endpoints (e.g. /queues) return `total` at the root.
    if (useOffsetPagination) return rootTotal
    return items.length
  }, [pagination, rootTotal, useOffsetPagination, items.length])
  const totalPages = useMemo(() => {
    if (pagination) return pagination.totalPages
    return Math.max(1, Math.ceil(total / PAGE_SIZE))
  }, [pagination, total])

  const fetchItems = useCallback(
    async (search: string, page: number): Promise<void> => {
      setLoading(true)
      try {
        if (clientSide) {
          // Fetch all items at once — no search/page/limit params.
          const url = extraSearchParams
            ? `${endpoint}?${extraSearchParams}`
            : endpoint
          const response = await api.get<Record<string, unknown>>(url)
          const rows = response[responseKey]
          const all = Array.isArray(rows) ? (rows as T[]) : []
          setAllItems(all)
          setSummary((response['summary'] as ListSummary | undefined) ?? null)
          setPagination(null)
          setRootTotal(all.length)
        } else {
          const params = new URLSearchParams()
          if (search) params.append('search', search)
          if (useOffsetPagination) {
            params.append('offset', String((page - 1) * PAGE_SIZE))
          } else {
            params.append('page', String(page))
          }
          params.append('limit', String(PAGE_SIZE))
          if (includeDeleted) params.append('includeDeleted', 'true')
          if (extraSearchParams) {
            const extra = new URLSearchParams(extraSearchParams)
            for (const [k, v] of extra) params.append(k, v)
          }

          const response = await api.get<Record<string, unknown>>(
            `${endpoint}?${params.toString()}`,
          )
          const rows = response[responseKey]
          setItems(Array.isArray(rows) ? (rows as T[]) : [])
          setSummary((response['summary'] as ListSummary | undefined) ?? null)
          if (useOffsetPagination) {
            setRootTotal(typeof response['total'] === 'number' ? (response['total'] as number) : 0)
            setPagination(null)
          } else {
            setPagination((response['pagination'] as ListPagination | undefined) ?? null)
            setRootTotal(0)
          }
        }
      } catch (error) {
        console.error(`Error fetching ${entityLabel}:`, error)
        setItems([])
        setAllItems([])
        setSummary(null)
        setPagination(null)
        setRootTotal(0)
      } finally {
        setLoading(false)
      }
    },
    [endpoint, entityLabel, responseKey, includeDeleted, extraSearchParams, useOffsetPagination, clientSide],
  )

  // Debounced refetch whenever the query, page, includeDeleted, or extra
  // params change. `offset` is what the shared pagination bar speaks; the API
  // is 1-indexed by page.
  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchItems(searchQuery, Math.floor(offset / PAGE_SIZE) + 1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchQuery, offset, fetchItems])

  // Notify the parent page whenever the fetched item list changes, so it can
  // derive values its drawer needs (e.g. hot-desking's active agent IDs).
  useEffect(() => {
    onDataChange?.(clientSide ? allItems : items)
  }, [clientSide, allItems, items, onDataChange])

  // --- Client-side filtering + pagination -----------------------------------
  // When `clientSide` is true, the full list is fetched once and then
  // filtered/sliced here on every search or page change — no refetch needed.
  const clientSideVisible = useMemo(() => {
    if (!clientSide) return null
    const q = searchQuery.trim().toLowerCase()
    let filtered = allItems
    if (q && searchFilter) {
      filtered = allItems.filter((item) => searchFilter(item, q))
    }
    return filtered
  }, [clientSide, allItems, searchQuery, searchFilter])

  const clientSideTotal = clientSideVisible?.length ?? 0
  const clientSideTotalPages = Math.max(1, Math.ceil(clientSideTotal / PAGE_SIZE))
  const clientSideCurrentPage = Math.floor(offset / PAGE_SIZE) + 1

  const clientSidePaged = useMemo(() => {
    if (!clientSideVisible) return null
    const start = offset
    return clientSideVisible.slice(start, start + PAGE_SIZE)
  }, [clientSideVisible, offset])

  // In clientSide mode, the items shown in the table come from the local
  // slice; otherwise they come from the server response.
  const displayItems = clientSide ? (clientSidePaged ?? []) : items
  const displayTotal = clientSide ? clientSideTotal : total
  const displayTotalPages = clientSide ? clientSideTotalPages : totalPages
  const displayCurrentPage = clientSide ? clientSideCurrentPage : currentPage

  function refetch(): void {
    void fetchItems(searchQuery, Math.floor(offset / PAGE_SIZE) + 1)
  }

  function open(item: T | null, readOnly: boolean): void {
    setEditing(item)
    setViewOnly(readOnly)
    setDrawerOpen(true)
  }

  async function handleDelete(item: T): Promise<void> {
    await confirm({
      title: `Delete ${title}`,
      message: deleteMessage(item),
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await api.delete(deleteEndpoint ? deleteEndpoint(item) : `${endpoint}/${item.id}`)
          toast.success('Deleted successfully')
          flashReloadNotice()
          refetch()
        } catch (err) {
          console.error(`Failed to delete ${entityLabel}:`, err)
          toast.error(`Failed to delete ${entityLabel}`)
        }
      },
    })
  }

  async function handleRestore(item: T): Promise<void> {
    try {
      await api.post(`${endpoint}/${item.id}/restore`)
      toast.success('Restored successfully')
      flashReloadNotice()
      refetch()
    } catch (err) {
      console.error(`Failed to restore ${entityLabel}:`, err)
      toast.error(`Failed to restore ${entityLabel}`)
    }
  }

  const defaultSummaryCards = useMemo<SummaryCard[]>(
    () => [
      { label: totalLabel, value: summary ? totalFrom(summary) : 0, accentColor: '#6366f1' },
      { label: 'Active', value: summary?.totalActive ?? 0, accentColor: '#10b981' },
      { label: 'Inactive', value: summary?.totalInactive ?? 0, accentColor: '#ef4444' },
    ],
    [summary, totalFrom, totalLabel],
  )

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value)
    setOffset(0)
  }

  const handleRefresh = (): void => {
    setSearchQuery('')
    setOffset(0)
    refetch()
  }

  const emptyMessage = searchQuery
    ? `No matching ${entityLabel}s found.`
    : emptyDescription

  // Determine whether a row is deleted (for restore button swap).
  const rowIsDeleted = useCallback(
    (item: T): boolean => {
      if (!canRestore || !isDeleted) return false
      return isDeleted(item)
    },
    [canRestore, isDeleted],
  )

  return (
    <>
      <AdminListScreenLayout>
        <div className="w-full space-y-6 px-4 pb-10">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h1>
              <p className="text-sm text-gray-400 mt-1">{description}</p>
            </div>
            {canManage && (
              <button
                type="button"
                onClick={() => open(null, false)}
                className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
              >
                <i className="pi pi-plus" />
                {addLabel}
              </button>
            )}
          </div>

          {/* Reload-required notice (SIP Trunks etc.) */}
          {reloadNoticeText && showReloadNotice && (
            <div
              className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              role="alert"
            >
              {reloadNoticeText}
            </div>
          )}

          {/* Stats */}
          {clientSide && clientSideTiles ? (
            clientSideTiles(allItems)
          ) : tiles !== undefined ? (
            tiles(summary)
          ) : (
            <SummaryCards cards={defaultSummaryCards} />
          )}

          {/* Toolbar */}
          <ListToolbar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onRefresh={handleRefresh}
            allButtonLabel={tableTitle}
            placeholder={searchPlaceholder}
            {...(searchAriaLabel ? { ariaLabel: searchAriaLabel } : {})}
            loading={loading}
          >
            {includeDeletedToggle && (
              <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-600">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 accent-[#3454cf]"
                  checked={includeDeleted}
                  onChange={(e) => { setIncludeDeleted(e.target.checked); setOffset(0) }}
                />
                Show deleted
              </label>
            )}
            {toolbarActions ?? null}
          </ListToolbar>

          {/* Table */}
          <DataTable
            value={displayItems}
            loading={loading}
            emptyMessage={emptyMessage}
            className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
          >
            {columns.map((col) => {
              const isFunction = typeof col.accessor === 'function'
              const field = isFunction ? (col.sortField ?? undefined) : String(col.accessor)
              const canSort = !isFunction || !!col.sortField
              return (
                <Column
                  key={String(col.label)}
                  header={col.label}
                  field={field}
                  sortField={col.sortField}
                  body={(row: T) =>
                    isFunction
                      ? (col.accessor as (row: T) => ReactNode)(row)
                      : String(row[col.accessor as keyof T] ?? '—')
                  }
                  sortable={canSort}
                />
              )
            })}
            <Column
              header="Actions"
              body={(row: T) => (
                <div className="flex items-center gap-2">
                  {customActions?.(row)}
                  {!hideRowActions && (
                    <ActionButtons
                      onView={() => open(row, true)}
                      {...(canManage && !rowIsDeleted(row) ? { onEdit: () => open(row, false) } : {})}
                      {...(canManage && rowIsDeleted(row) && canRestore
                        ? { onRestore: () => void handleRestore(row) }
                        : {})}
                      {...(canManage && !rowIsDeleted(row)
                        ? { onDelete: () => void handleDelete(row) }
                        : {})}
                    />
                  )}
                </div>
              )}
              style={{ width: hideRowActions && customActions ? '120px' : '160px' }}
            />
          </DataTable>

          {/* Pagination */}
          {displayTotal > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Showing {((displayCurrentPage - 1) * PAGE_SIZE) + 1} to {Math.min(displayCurrentPage * PAGE_SIZE, displayTotal)} of {displayTotal} {entityLabel}{displayTotal !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={displayCurrentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, displayTotalPages) }, (_, i) => {
                    let pageNum: number
                    if (displayTotalPages <= 5) {
                      pageNum = i + 1
                    } else if (displayCurrentPage <= 3) {
                      pageNum = i + 1
                    } else if (displayCurrentPage >= displayTotalPages - 2) {
                      pageNum = displayTotalPages - 4 + i
                    } else {
                      pageNum = displayCurrentPage - 2 + i
                    }
                    return (
                      <button
                        type="button"
                        key={pageNum}
                        onClick={() => setOffset((pageNum - 1) * PAGE_SIZE)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-all ${
                          displayCurrentPage === pageNum
                            ? 'bg-[#3454cf] text-white'
                            : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>
                <button
                type="button"
                onClick={() => setOffset(Math.min((displayTotalPages - 1) * PAGE_SIZE, offset + PAGE_SIZE))}
                disabled={displayCurrentPage === displayTotalPages}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
              </div>
            </div>
          )}
        </div>
      </AdminListScreenLayout>

      {renderDrawer({
        open: drawerOpen,
        item: editing,
        readOnly: viewOnly,
        onClose: () => setDrawerOpen(false),
        onRefresh: () => {
          flashReloadNotice()
          refetch()
        },
        onItemChange: (next) => setEditing(next),
        onSaved: () => {
          setDrawerOpen(false)
          flashReloadNotice()
          refetch()
        },
      })}
      <Dialog />
    </>
  )
}
